import React, { useEffect, useMemo, useState } from 'react'
import { checkApiStatus } from '../../../../utils';
import { globalBetaGetService } from '../../../../utils/globalApiServices';
import { Box, Grid } from '@mui/material';
import moment from 'moment';
import { getWhatsNewApi } from '../apiServices';
import { PageLoader } from '../../../../shared_elements';
import RecordsIcon from '../../../../assets/img/RecordsIcom0.svg';
import ProjectsIcon from '../../../../assets/img/ProjectsIcon.svg';
import CommVergeIcon from '../../../../assets/img/acumen_commverge0.png';
import FinSIcon from '../../../../assets/img/valuation_new1.png';
import LeaseIcon from '../../../../assets/img/LeaseIcon.b43f0fc5.svg';
import AirworthinessIcon from '../../../../assets/img/CAMO_Final_Logo.svg';
import MaintenanceIcon from '../../../../assets/img/MaintenanceIcon.svg';
import OpticIcon from '../../../../assets/img/optic_logo.d36921a8.svg';
import WhatsNewCard from '../components/WhatsNewCard';
import WhatsNewToolbar from '../components/WhatsNewToolbar';
import ReleaseTimeline from '../components/ReleaseTimeline';
import UpcomingFeaturesCard from '../components/UpcomingFeaturesCard';
import RecentReleasesCard from '../components/RecentReleasesCard';
import ApplicationsActivityCard from '../components/ApplicationsActivityCard';
import '../styles/whatsNew.scss';

const searchableText = (item) => `${item?.title || ''} ${item?.description || ''} ${item?.application?.label || ''}`
    .replace(/<[^>]*>/g, ' ')
    .toLowerCase();

const applicationFilters = [
    { value: 'all', label: 'All', keywords: [] },
    { value: 'lease', label: 'Lease Management', keywords: ['lease management'] },
    { value: 'projects', label: 'Projects Management', keywords: ['project management', 'projects management', 'projects'] },
    { value: 'records', label: 'Records Dataroom', keywords: ['records dataroom', 'records', 'dataroom'] },
    { value: 'maintenance', label: 'Maintenance Claims', keywords: ['maintenance claim management', 'maintenance claims'] },
    { value: 'commverge', label: 'CommVerge', keywords: ['commverge'] },
    { value: 'fin-s', label: 'fin-S', keywords: ['fin-s', 'fin s'] },
    { value: 'airworthiness', label: 'Airworthiness', keywords: ['airworthiness management', 'airworthiness'] },
    { value: 'optic', label: 'Teardown & Consignment', keywords: ['teardown', 'consignment', 'optic'] }
];
const applicationColors = ['#6252c7', '#dd6b20', '#087ea4', '#c44f76', '#1676a5', '#db8c12', '#16866f', '#70843b'];

const WhatsNew = () => {
    const [whatsNewInfo, setWhatsNewInfo] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [hasLoadError, setLoadError] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedApplication, setSelectedApplication] = useState('all');

    useEffect(() => {
        getWhatsNewList('fetch', { application: 1, timestamp: Date.now() });
    }, []);

    const getWhatsNewList = (loaderType, query = {}) => {
        setLoading(true);
        globalBetaGetService(getWhatsNewApi({ application: 1, timestamp: Date.now() })).then(response => {
            if (checkApiStatus(response)) {
                setLoading(false);
                setWhatsNewInfo(response.data.data);
            } else {
                setLoading(false);
            }
        })
    };

    const activeApplication = applicationFilters.find(filter => filter.value === selectedApplication);
    const sortedUpdates = useMemo(() => [...whatsNewInfo].sort((firstItem, secondItem) => (
        moment(secondItem?.release_date).valueOf() - moment(firstItem?.release_date).valueOf()
    )), [whatsNewInfo]);
    const normalizedSearch = searchValue.trim().toLowerCase();
    const filteredUpdates = useMemo(() => sortedUpdates.filter(item => {
        const itemText = searchableText(item);
        const matchesSearch = itemText.includes(normalizedSearch);
        const matchesApplication = selectedApplication === 'all' || activeApplication?.keywords.some(keyword => itemText.includes(keyword));

        return matchesSearch && matchesApplication;
    }), [activeApplication, normalizedSearch, selectedApplication, sortedUpdates]);
    const getApplicationCount = (filter) => filter.value === 'all'
        ? sortedUpdates.length
        : sortedUpdates.filter(item => filter.keywords.some(keyword => searchableText(item).includes(keyword))).length;
    const latestUpdate = sortedUpdates[0];
    const accentColors = [
        { main: '#6252c7', soft: '#f0edff', glow: 'rgba(98, 82, 199, 0.16)' },
        { main: '#087ea4', soft: '#e6f7fb', glow: 'rgba(8, 126, 164, 0.14)' },
        { main: '#dd6b20', soft: '#fff3e8', glow: 'rgba(221, 107, 32, 0.14)' },
        { main: '#16866f', soft: '#e8f8f3', glow: 'rgba(22, 134, 111, 0.14)' }
    ];
    const upcomingFeatures = [
        { title: 'AI-powered lease abstraction', application: 'Lease Management', eta: 'Aug 2026', status: 'In development: The feature is currently being built', color: '#6252c7' },
        { title: 'Smart records search', application: 'Records Dataroom', eta: 'Aug 2026', status: 'User testing: Selected users are evaluating the feature', color: '#087ea4' },
        { title: 'Fleet health dashboard', application: 'Airworthiness', eta: 'Aug 2026', status: 'On the roadmap: Planned for a future release', color: '#16866f' },
        { title: 'Automated claims triage', application: 'Maintenance Claims', eta: 'Sep 2026', status: 'On the roadmap: Planned for a future release', color: '#dd6b20' },
        { title: 'Portfolio benchmarking', application: 'Projects Management', eta: 'Oct 2026', status: 'In development: The feature is currently being built', color: '#c44f76' }
    ];
    const applicationActivity = useMemo(() => {
        const today = moment().endOf('day');
        const activityPeriodStart = moment().subtract(90, 'days').startOf('day');
        const recentUpdates = sortedUpdates.filter(item => {
            const releaseDate = moment(item?.release_date);
            return releaseDate.isValid() && releaseDate.isBetween(activityPeriodStart, today, undefined, '[]');
        });

        return applicationFilters.slice(1).map((application, index) => ({
            name: application.label,
            color: applicationColors[index],
            count: recentUpdates.filter(item => (
                application.keywords.some(keyword => searchableText(item).includes(keyword))
            )).length
        }));
    }, [sortedUpdates]);
    const getReleaseArtwork = (item) => {
        const releaseText = searchableText(item);
        const releaseTitle = (item?.title || '').trim().toLowerCase();
        const artworkOptions = [
            { keywords: ['records dataroom', 'records'], artwork: { src: RecordsIcon, alt: 'Records Dataroom' } },
            { keywords: ['project management', 'projects management'], artwork: { src: ProjectsIcon, alt: 'Projects Management' } },
            { keywords: ['maintenance claim management', 'maintenance claims'], artwork: { src: MaintenanceIcon, alt: 'Maintenance Claim Management' } },
            { keywords: ['commverge'], artwork: { src: CommVergeIcon, alt: 'CommVerge' } },
            { keywords: ['fin-s', 'fin s'], artwork: { src: FinSIcon, alt: 'fin-S' } },
            { keywords: ['lease management'], artwork: { src: LeaseIcon, alt: 'Lease Management' } },
            { keywords: ['airworthiness management', 'airworthiness'], artwork: { src: AirworthinessIcon, alt: 'Airworthiness Management' } },
            { keywords: ['teardown', 'consignment', 'optic'], artwork: { src: OpticIcon, alt: 'Teardown and Consignment Program Management' } }
        ];

        if (releaseTitle.includes('home')) return { isHome: true, alt: 'Home Page' };
        const titleMatch = artworkOptions.find(option => option.keywords.some(keyword => releaseTitle.includes(keyword)));
        if (titleMatch) return titleMatch.artwork;

        const contentMatch = artworkOptions.find(option => option.keywords.some(keyword => releaseText.includes(keyword)));
        if (contentMatch) return contentMatch.artwork;

        return null;
    };
    const latestArtwork = latestUpdate ? getReleaseArtwork(latestUpdate) : null;
    const getReleaseStatus = (item) => {
        const apiStatus = item?.release_status ?? item?.releaseStatus ?? item?.status;
        const normalizedStatus = String(apiStatus ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ');
        const isReleased = typeof item?.is_released === 'boolean'
            ? item.is_released
            : ['released', 'published', 'live', 'completed', 'complete'].includes(normalizedStatus)
            || (!normalizedStatus && moment(item?.release_date).isSameOrBefore(moment(), 'day'));

        return isReleased
            ? { label: 'Released', className: 'is-released' }
            : { label: 'In Progress', className: 'is-in-progress' };
    };
    const leaseManagementHighlights = ['Improved contract and asset setup', 'Streamlined invoice workflows', 'Better receivables visibility', 'Updated reporting and usability'];

    const getReleaseEnhancement = (item) => {
        const itemText = searchableText(item);
        if (itemText.includes('lease management')) {
            return { highlights: leaseManagementHighlights, module: 'Lease Management', users: 'Authorised roles' };
        }
        return null;
    };

    const scrollToReleases = () => document.getElementById('release-highlights')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    return (
        <Box className="whats-new" style={{ height: `${window.innerHeight - 70}px` }}>
            <WhatsNewCard
                latestArtwork={latestArtwork}
                latestUpdate={latestUpdate}
                sortedUpdatesCount={sortedUpdates.length}
                activeApplicationsCount={applicationFilters.length - 1}
                onExploreClick={scrollToReleases}
            />

            <Box className="whats-new-content">
                <WhatsNewToolbar
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    applicationFilters={applicationFilters}
                    selectedApplication={selectedApplication}
                    onSelectApplication={setSelectedApplication}
                    getApplicationCount={getApplicationCount}
                />

                <Box className="whats-new-body">
                    <Grid container spacing={2.5} alignItems="flex-start">
                        <Grid size={{ xs: 12, lg: 8.5 }}>
                            <ReleaseTimeline
                                filteredUpdates={filteredUpdates}
                                sortedUpdatesLength={sortedUpdates.length}
                                latestUpdate={latestUpdate}
                                hasLoadError={hasLoadError}
                                isLoading={isLoading}
                                accentColors={accentColors}
                                getReleaseArtwork={getReleaseArtwork}
                                getReleaseStatus={getReleaseStatus}
                                getReleaseEnhancement={getReleaseEnhancement}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, lg: 3.5 }}>
                            <Box className="whats-new-sidebar">
                                <UpcomingFeaturesCard features={upcomingFeatures} />
                                <RecentReleasesCard updates={sortedUpdates} />
                                <ApplicationsActivityCard applications={applicationActivity} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            {isLoading ? <PageLoader /> : null}
        </Box>
    )
}

export default WhatsNew;